import React, { useEffect } from 'react'
import { DropzoneOptions, FileWithPath, useDropzone } from 'react-dropzone'
import styled from 'styled-components'
import DeleteIcon from '@mui/icons-material/Delete'
import { IconButton } from '@mui/material'
import { Flex } from './Flex'
import { colors, spacing } from '../theme'
import { Body1, Body1Bold, Body2 } from './Typography'
import { SnackAlert } from './SnackAlert'
import { captureException } from '../utils/Sentry'

interface colorProps {
  $isDragAccept: boolean
  $isDragReject: boolean
}

const getColor = (props: colorProps) => {
  if (props.$isDragReject) {
    return colors.red
  }
  if (props.$isDragAccept) {
    return colors.deepGreen
  }
  return colors.granit
}

const Zone = styled.div<colorProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${spacing.medium};
  border-width: 2px;
  border-radius: 2px;
  border-color: ${(props) => getColor(props)};
  border-style: dashed;
  background-color: ${colors.svalnaGray1};
  color: ${colors.granit};
  outline: none;
  transition: border 0.24s ease-in-out;
  width: 100%;
  cursor: pointer;
  gap: ${spacing.medium};
`

export interface DropzoneProps {
  text: string
  multiple?: boolean
  fileTypesKey?: string
  fileTypes?: string[]
  files: FileWithPath[]
  addFiles: (files: readonly FileWithPath[]) => void
  removeFile: (file: FileWithPath) => void
}

export function Dropzone({
  text,
  multiple,
  fileTypesKey,
  fileTypes,
  files,
  addFiles,
  removeFile,
}: DropzoneProps): React.JSX.Element {
  const dropZoneOption: DropzoneOptions = {
    multiple,
    maxSize: 1073741824, // 1 GB
  }
  if (fileTypesKey && fileTypes) {
    dropZoneOption.accept = { [`${fileTypesKey}`]: fileTypes }
  }
  const {
    getRootProps,
    getInputProps,
    acceptedFiles,
    fileRejections,
    isDragActive,
  } = useDropzone(dropZoneOption)

  const [$isDragAccept, setIsSuccess] = React.useState(false)
  const [$isDragReject, setIsError] = React.useState(false)
  const [error, setError] = React.useState('')

  useEffect(() => {
    if (acceptedFiles.length > 0) {
      setIsSuccess(true)
      addFiles(acceptedFiles)
    }
  }, [acceptedFiles, addFiles])

  useEffect(() => {
    if (fileRejections.length > 0) {
      setIsError(true)
    } else {
      setIsError(false)
    }
  }, [fileRejections])

  useEffect(() => {
    if (isDragActive) {
      setIsSuccess(false)
      setIsError(false)
    }
  }, [isDragActive])

  const handleRemoveClick = (file: FileWithPath) => {
    removeFile(file)
  }

  useEffect(() => {
    if (fileRejections.length > 0) {
      // we display the first error if the user retry with a file that triggered a secondary error they will see the error then
      if (fileRejections[0].errors[0]) {
        const errorCode = fileRejections[0].errors[0].code
        if (errorCode === 'file-invalid-type') {
          setError(`Filerna måste vara av typen ${fileTypes}`)
        } else if (errorCode === 'file-too-large') {
          setError('Filerna måste vara mindre än 1 GB')
        } else if (errorCode === 'too-many-files') {
          setError('För många filer')
        } else {
          // should not happen because we are only setting restriction onf file type and size and these error are handled above
          captureException(new Error(JSON.stringify(fileRejections)))
          setError('Något gick fel, försök igen')
        }
      } else {
        // should never happen because all entries should have at least one error in errors
        captureException(new Error(JSON.stringify(fileRejections)))
        setError('Något gick fel, försök igen')
      }
    } else {
      setError('')
    }
  }, [fileRejections, fileTypes])

  return (
    <>
      <SnackAlert open={!!error} severity='error' onClose={() => setError('')}>
        <Body1>{error}</Body1>
      </SnackAlert>
      <Zone
        {...getRootProps({ $isDragAccept, $isDragReject })}
        data-testid={`dropzone-${text.substring(0, 15)}`}
      >
        <input {...getInputProps()} />
        <Body1Bold>{`${text}`}</Body1Bold>
        {files.length !== 0 && (
          <Flex column>
            <Body1>Valda filer:</Body1>
            {files.map((file) => (
              <Flex key={file.path} itemsCenter>
                <Body2 key={file.path}>{file.name}</Body2>
                <IconButton
                  key={`remove-${file.path}`}
                  aria-label={`remove ${file.path}`}
                  onClick={(event) => {
                    event.stopPropagation() // do not trigger the dropzone click
                    handleRemoveClick(file)
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Flex>
            ))}
          </Flex>
        )}
      </Zone>
    </>
  )
}
